import React, { useEffect, useState } from "react";
import "./ProductListContainer.less";
import { Button, Checkbox, Col, DatePicker, Divider, Form, Row, Space } from "antd";
import { Link, useParams, useRouteMatch } from "react-router-dom";
import moment from "moment";
import { uniqBy } from "lodash";
import plusIcon from "../../../img/plus.svg";
import { checkIsLoaded, displayFormat } from "../../../lib/utils";
import {
  ServiceListFilterValues,
  ServiceListFilterValuesChange,
} from "./ProductListContainerInterface";
import ProductsTable from "../../../components/ProductsTable/ProductsTable";
import { useAppSelector } from "../../..";
import { hasAccess } from "../../../components/RolesModule/hasAccess";
import { MENU_ACCOUNT_SERVICES_WRITE } from "../../../lib/const/permissions";
import HistoryAndPropertyModal from "../../../components/HistoryAndPropertyModal/HistoryAndPropertyModal";
import BlockingModal from "../../../components/BlockingModal/BlockingModal";
import { BlockingObjTypes } from "../../../lib/const/blockingObjTypes";

interface ComponentProps {
  loadProducts: (props?: ServiceListFilterValues) => void;
  loadProductVersions: (props: { id: number }) => void;
  serviceListFilterValues: ServiceListFilterValues;
  handleSetServiceListFilterValues: (props: ServiceListFilterValuesChange) => void;
}

export const ProductListContainer = ({
  loadProducts,
  loadProductVersions,
  serviceListFilterValues,
  handleSetServiceListFilterValues,
}: ComponentProps) => {
  const { productsList } = useAppSelector((state) => state.products);
  const { agreements, agreementsLoading } = useAppSelector((state) => state.agreements);
  const { url } = useRouteMatch();

  const [visibleModal, setVisibleModal] = useState(false);
  const [objectId, setObjectId] = useState(null);
  const [objectDateFrom, setObjectDateFrom] = useState("");
  const [objectDateTo, setObjectDateTo] = useState("");

  // Загрузка продуктов
  useEffect(() => {
    const agreementsLoaded = checkIsLoaded(agreementsLoading);
    if (agreementsLoaded) {
      loadProducts();
    }
  }, [
    agreementsLoading,
    serviceListFilterValues.isSearchIncludePastDates,
    serviceListFilterValues.onDate,
  ]);

  if (productsList && productsList.length !== 0) {
    const sortedServices = [...productsList].sort((a, b) =>
      moment(a.fromDate).isAfter(b.fromDate) ? 1 : -1
    );
    const uniqSortedServices = uniqBy(sortedServices, ({ id }) => id);
    uniqSortedServices.reverse();
  }

  const handleShowModal = (objId) => {
    setVisibleModal(true);
    setObjectDateFrom(objId.fromDate);
    setObjectDateTo(objId.toDate);
    setObjectId(objId.id);
  };

  const handleHideModal = () => {
    setVisibleModal(false);
    setObjectId(null);
  };

  const { accountId } = useParams<{ accountId: string }>();

  const { account } = useAppSelector((state) => state.accounts);

  const accountName = account.individual
    ? [account.individual?.lastName, account.individual?.firstName, account.individual?.middleName]
        .filter(Boolean)
        .join(" ")
    : account.organization?.fullName;

  const [objectBlockingId, setObjectBlockingId] = useState<{
    id?: number;
    fullName?: string;
    agreementNumber?: number;
    agreementId?: number;
    name?: string;
    toDate?: string;
    accountId?: number;
  }>({});

  const handleShowBlockingModal = (objId) => {
    setObjectBlockingId(objId);
  };

  const handleHideBlockingModal = () => {
    setObjectBlockingId({});
  };

  const handleSubmit = ({ onDate, isSearchIncludePastDates }) => {
    handleSetServiceListFilterValues({
      onDate,
      isSearchIncludePastDates,
      currentSortOrder: isSearchIncludePastDates ? { toDate: "descend" } : { fromDate: "ascend" },
    });
  };

  const renderOnDateField = () => {
    const itemStyle = { marginBottom: "0" };
    return (
      <Form onFinish={handleSubmit} style={{ display: "flex" }}>
        <Space>
          <Form.Item
            name="isSearchIncludePastDates"
            valuePropName="checked"
            style={itemStyle}
            initialValue={serviceListFilterValues.isSearchIncludePastDates}
          >
            <Checkbox id="isSearchIncludePastDates" />
          </Form.Item>
          <Form.Item style={itemStyle}>
            <label
              htmlFor="isSearchIncludePastDates"
              style={{ cursor: "pointer", marginRight: "50px" }}
            >
              Показать закрытые продукты
            </label>
          </Form.Item>
          <Form.Item name="onDate" style={itemStyle} initialValue={serviceListFilterValues.onDate}>
            <DatePicker allowClear={false} format={displayFormat} />
          </Form.Item>
          <Form.Item style={itemStyle}>
            <Button type="primary" className="account-service-list__onDateBtn" htmlType="submit">
              Применить
            </Button>
          </Form.Item>
        </Space>
      </Form>
    );
  };

  return (
    <div className="product-list-container">
      <Row justify="space-between">
        <Col>
          <h1>Продукты</h1>
        </Col>
      </Row>

      <Row justify="space-between">
        <Col span={12}>
          {hasAccess(MENU_ACCOUNT_SERVICES_WRITE) && (
            <Link to={`${url.replace("services", "products")}/create`}>
              <Button
                className="product-list-container__addBtn"
                icon={<img src={plusIcon} width="18px" alt="plus" />}
                type="text"
              >
                Подключить продукт
              </Button>
            </Link>
          )}
        </Col>
        {renderOnDateField()}
      </Row>
      <Divider className="product-list-container__divider" />
      <ProductsTable
        handleShowHistoryModal={handleShowModal}
        loadProductVersions={loadProductVersions}
        handleSetServiceListFilterValues={handleSetServiceListFilterValues}
        serviceListFilterValues={serviceListFilterValues}
        handleShowBlockingModal={handleShowBlockingModal}
      />
      <HistoryAndPropertyModal
        objectTypeSysname="service"
        objectId={objectId}
        handleHideModal={handleHideModal}
        visibleModal={visibleModal}
        dateObjectFromForCalendar={objectDateFrom}
        dateObjectToForCalendar={objectDateTo}
        hasAccessToEdit={hasAccess(MENU_ACCOUNT_SERVICES_WRITE)}
      />
      <BlockingModal
        objectTypeId={BlockingObjTypes.Продукт}
        objectId={objectBlockingId?.id}
        handleHideModal={handleHideBlockingModal}
        visibleModal={!!objectBlockingId?.id}
        accountName={accountName}
        serviceAgreementId={objectBlockingId?.agreementId}
        agreementNumber={objectBlockingId?.agreementNumber}
        serviceAgreementAccountId={objectBlockingId?.accountId || Number(accountId)}
        serviceName={objectBlockingId?.name}
        dateObjectToForCalendar={objectBlockingId?.toDate}
      />
    </div>
  );
};
